'use client';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';

interface Props {
  emailVerified: boolean;
  onResend: () => Promise<void>;
  onDismiss: () => void;
  className?: string;
}

export function VerifyEmailBanner({ emailVerified, onResend, onDismiss, className }: Props) {
  const [pending, setPending] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Tick every second while cooling so the countdown re-renders. `now`'s
  // only purpose is to force a re-render — the displayed seconds is
  // derived from cooldownUntil - Date.now().
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (cooldownUntil === null || cooldownUntil <= Date.now()) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [cooldownUntil]);

  if (emailVerified) return null;

  const cooling = cooldownUntil !== null && cooldownUntil > now;

  async function handleResend() {
    setError(null);
    setPending(true);
    try {
      await onResend();
      setCooldownUntil(Date.now() + 60_000);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      role="region"
      aria-label="email verification"
      className={clsx(
        'flex items-center gap-3 bg-coral/15 text-brown text-sm px-4 py-2 border-b border-coral/30',
        className,
      )}
    >
      <span className="flex-1">
        Email Anda belum diverifikasi.
        {error && <span className="text-rose ml-2">({error})</span>}
      </span>
      <button
        type="button"
        onClick={handleResend}
        disabled={pending || cooling}
        className="font-semibold text-coral hover:text-rose disabled:text-brown-md/60 disabled:cursor-not-allowed"
      >
        {pending
          ? 'Mengirim…'
          : cooling
            ? `Kirim ulang dalam ${Math.ceil((cooldownUntil! - now) / 1000)}s`
            : 'Kirim ulang link'}
      </button>
      <button
        type="button"
        aria-label="tutup banner"
        onClick={onDismiss}
        className="text-brown-md hover:text-brown px-2"
      >
        ×
      </button>
    </div>
  );
}
